import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Occlusion from 'src/components/Occlusion/Occlusion'
import SvgDownload from 'src/components/Svgs/SvgDownload'
import Trns from 'src/localization/Trns'
import { expensesTransactionsUrl } from 'src/navigation/urls'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import {
  selectAttachmentFromState,
  selectDeleteActionFromState,
} from 'src/redux/selectors/app.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { PopupFadeInAndExpand } from 'src/style/keyframeUtils'
import { css } from 'styled-components'

interface AttachmentPopupProps {}

const AttachmentPopup: React.FC<
  AttachmentPopupProps
> = (): React.ReactElement => {
  const deleteAction = useSelector(selectDeleteActionFromState)
  const attachment = useSelector(selectAttachmentFromState)
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const dispatch = useDispatch()

  const onDeleteClick = (): void => {
    if (deleteAction !== undefined) {
      dispatch(createSetIsAttachmentPopupOpen(false))
      deleteAction()
      matchPath(
        calculatedBackgroundLocation.pathname,
        expensesTransactionsUrl(),
      ) &&
        analytics?.track(
          'transactions - delete transactions attachment - button press',
          {
            screen: '/transactions',
            interaction: 'button press',
            intent: 'delete transactions attachment',
          },
        )
    }
  }

  return (
    <div
      css={css`
        position: fixed;
        z-index: 12;
        top: 0;
        width: 100vw;
        height: 100vh;
        overflow: scroll;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      `}
    >
      <Occlusion
        isOpen
        onClick={(): void => {
          dispatch(createSetIsAttachmentPopupOpen(false))
        }}
        fadeIn
      />
      {attachment !== undefined && (
        <div
          css={css`
            z-index: 1;
            display: flex;
            flex-direction: column;
            ${PopupFadeInAndExpand()};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin: 60px 10px;
            `)};

            ${getBreakpointOnly(breakpointSizes.XXSM)(css`
              margin: 40px 5px;
              align-self: stretch;
            `)}
          `}
        >
          <img
            css={css`
              max-width: 600px;
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                width: 100%;
              `)}
            `}
            src={attachment.urls.main}
            alt={''}
          />
          <div
            css={css`
              height: 50px;
              max-width: 600px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              background-color: ${styleConstants.colors.gray_lightest};
              padding: 16px;
              border-bottom-left-radius: ${styleConstants.borderRadius.box};
              border-bottom-right-radius: ${styleConstants.borderRadius.box};

              ${deleteAction === undefined &&
              css`
                justify-content: flex-end;
              `}

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                width: 100%;
              `)}
            `}
          >
            {deleteAction !== undefined && (
              <span
                onClick={onDeleteClick}
                onKeyPress={onDeleteClick}
                role="button"
                tabIndex={0}
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.medium,
                  )};
                  cursor: pointer;

                  &:hover {
                    color: ${styleConstants.colors.red_dark};
                  }
                `}
              >
                <Trns source={'Delete'} />
              </span>
            )}
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
              `}
              href={attachment.urls.original}
              ariaLabelSource={'Download'}
            >
              <Trns source={'Download'} />
              <SvgDownload
                css={css`
                  transform: scale(calc(14 / 24));
                `}
              />
            </HoverableLink>
          </div>
        </div>
      )}
    </div>
  )
}

export default AttachmentPopup
